import React, { useState, useEffect } from 'react';
import { db, addOrder } from '../firebase';
import { collection, onSnapshot, addDoc, doc, updateDoc, deleteDoc, writeBatch, setDoc } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import PaymentOptionsScreen from '../PaymentPage';

// CSS לאנימציה
const animationStyle = `
@keyframes flyToCart {
    0% {
        transform: translate(-50%, -50%) scale(1);
        opacity: 1;
        offset-distance: 0%;
    }
    60% {
        transform: translate(-50%, -50%) scale(1.2);
        opacity: 0.8;
        offset-distance: 60%;
    }
    100% {
        transform: translate(-50%, -50%) scale(0.5);
        opacity: 0;
        offset-distance: 100%;
    }
}
.flying-cart-anim {
    position: fixed;
    z-index: 9999;
    pointer-events: none;
    offset-path: var(--arc-path);
    animation: flyToCart 2s cubic-bezier(.5,-0.5,.5,1.5) forwards;
}
`;

// הוסף את ה-CSS לדף
if (typeof document !== 'undefined') {
    if (!document.getElementById('fly-to-cart-style')) {
        const style = document.createElement('style');
        style.id = 'fly-to-cart-style';
        style.textContent = animationStyle;
        document.head.appendChild(style);
    }
}

// --- אייקונים ---
const PencilIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.536L16.732 3.732z" /></svg>;
const TrashIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>;
const PlusIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>;
const UploadIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>;
const CartIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 48 48" fill="none">
    <circle cx="15" cy="39" r="3" fill="black"/>
    <circle cx="35" cy="39" r="3" fill="black"/>
    <path d="M6 10H10L14 32H36L40 16H12" stroke="black" strokeWidth="3.5" strokeLinejoin="round" strokeLinecap="round" fill="none"/>
  </svg>
);

// --- קומפוננטת המוצרים ---
function ProductItem({ product, category, onSave, onDelete, isEditMode, addToCart, selectedQuantity, updateQuantity }) {
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState({ ...product });
    const [isOpen, setIsOpen] = useState(false);

    const handleSave = () => {
        onSave(product.id, editData);
        setIsEditing(false);
    };

    if (isEditing) {
        return (
            <div className="bg-gray-700/50 p-3 rounded-lg flex flex-col md:flex-row gap-4 items-center justify-between">
                <div className="flex-1 text-right">
                    <input type="text" value={editData.name} onChange={(e) => setEditData({...editData, name: e.target.value})} className="bg-gray-600 p-1 rounded w-full text-white" placeholder="שם מוצר" />
                </div>
                <div className="flex-1 text-right">
                    <input type="text" value={editData.sku} onChange={(e) => setEditData({...editData, sku: e.target.value})} className="bg-gray-600 p-1 rounded w-full text-white" placeholder="מק''ט" />
                </div>
                <div className="flex-1 text-right">
                    <input type="number" value={editData.price} onChange={(e) => setEditData({...editData, price: parseFloat(e.target.value) || 0})} className="bg-gray-600 p-1 rounded w-full text-white" placeholder="מחיר" />
                </div>
                <div className="flex-1 text-right">
                    <input type="number" value={editData.stock} onChange={(e) => setEditData({...editData, stock: parseInt(e.target.value, 10) || 0})} className="bg-gray-600 p-1 rounded w-full text-white" placeholder="מלאי" />
                </div>
                <div className="flex-1 text-right">
                    <input type="text" value={editData.customFieldValue} onChange={(e) => setEditData({...editData, customFieldValue: e.target.value})} className="bg-gray-600 p-1 rounded w-full text-white" placeholder={category.customFieldName} />
                </div>
                <div className="flex-1 text-right">
                    <input type="text" value={editData.imageUrl || ''} onChange={(e) => setEditData({...editData, imageUrl: e.target.value})} className="bg-gray-600 p-1 rounded w-full text-white" placeholder="קישור לתמונה (URL)" />
                </div>
                <div className="flex gap-2 w-20 justify-center">
                <button onClick={handleSave} className="bg-green-500 text-white p-2 rounded-full hover:bg-green-600"><CheckIcon /></button>
                <button onClick={() => setIsEditing(false)} className="bg-gray-500 text-white p-2 rounded-full hover:bg-gray-600"><XIcon /></button>
                </div>
            </div>
        );
    }

    return (
        <div
            className="bg-gray-700/50 p-3 rounded-lg flex flex-col md:flex-row gap-4 items-center justify-between cursor-pointer"
            onClick={() => setIsOpen((prev) => !prev)}
        >
            <div className="flex-1 text-right">
                <p className="font-bold">{product.name}</p>
            </div>
            <div className="flex-1 text-right">
                <p className="text-sm text-gray-400">{product.sku}</p>
            </div>
            <div className="flex-1 text-right">
                <p>{product.price} ₪</p>
            </div>
            <div className="flex-1 text-right">
                <p className="text-sm text-gray-400">{product.stock}</p>
            </div>
            <div className="flex-1 text-right">
                <p>{product.customFieldValue}</p>
            </div>
            {/* תמונה מוקטנת - תוצג רק כאשר השורה פתוחה ויש imageUrl תקין */}
            {isOpen && product.imageUrl && typeof product.imageUrl === 'string' && product.imageUrl.startsWith('http') && (
                <div className="flex items-center justify-center" style={{ minWidth: 80, minHeight: 80 }}>
                    <img
                        src={product.imageUrl}
                        alt="תמונה"
                        style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 6, border: '1px solid #ccc', background: '#222' }}
                    />
                </div>
            )}
            {isEditMode && (
                <div className="flex gap-2 w-20 justify-center">
                <button onClick={() => setIsEditing(true)} className="p-2 text-gray-400 hover:text-white"><PencilIcon /></button>
                <button onClick={() => onDelete(product.id)} className="p-2 text-red-400 hover:text-red-500"><TrashIcon /></button>
            </div>
            )}
            {!isEditMode && (
                <div className="flex items-center gap-2 w-32 justify-center">
                    {selectedQuantity > 0 && (
                        <>
                            <button onClick={() => updateQuantity(product.id, selectedQuantity - 1)} className="bg-gray-600 hover:bg-gray-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">-</button>
                            <span className="text-white font-bold">{selectedQuantity}</span>
                        </>
                    )}
                    <button onClick={(e) => addToCart(product, e, selectedQuantity)} className="bg-gray-600 hover:bg-gray-500 text-white px-3 py-1 rounded text-sm">+</button>
                </div>
            )}
        </div>
    );
}
const CheckIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>;
const XIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>;


// --- קומפוננטת הקטגוריה ---
function CategorySection({ category, products, onSave, onDelete, onProductUpdate, onProductDelete, onProductAdd, onExcelImport, isEditMode, addToCart, selectedItems, updateQuantity }) {
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState({ name: category.name, customFieldName: category.customFieldName });
    const fileInputRef = React.createRef();

    const handleSave = () => {
        onSave(category.id, editData);
        setIsEditing(false);
    };

    const handleFileImport = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (evt) => {
            const bstr = evt.target.result;
            const wb = XLSX.read(bstr, { type: 'binary' });
            const wsname = wb.SheetNames[0];
            const ws = wb.Sheets[wsname];
            const data = XLSX.utils.sheet_to_json(ws, { header: 1 });
            onExcelImport(category.id, data);
        };
        reader.readAsBinaryString(file);
    };

    return (
        <div className="bg-gray-800 p-4 rounded-lg mb-4">
            {/* כותרת הקטגוריה */}
            <div className="flex justify-between items-center mb-4">
                {isEditing ? (
                    <div className="flex gap-2 items-center flex-1">
                        <input type="text" value={editData.name} onChange={(e) => setEditData({...editData, name: e.target.value})} className="bg-gray-700 p-2 rounded text-white text-xl font-bold flex-1" placeholder="שם קטגוריה" />
                        <input type="text" value={editData.customFieldName} onChange={(e) => setEditData({...editData, customFieldName: e.target.value})} className="bg-gray-700 p-2 rounded text-white flex-1" placeholder="שם שדה מותאם" />
                    </div>
                ) : (
                    <h3 className="text-xl font-bold text-amber-400">{category.name}</h3>
                )}
                {isEditMode && (
                <div className="flex gap-2">
                        {isEditing ? (
                        <>
                            <button onClick={handleSave} className="p-2 text-green-400 hover:text-green-500"><CheckIcon /></button>
                            <button onClick={() => setIsEditing(false)} className="p-2 text-gray-400 hover:text-white"><XIcon /></button>
                        </>
                    ) : (
                        <button onClick={() => setIsEditing(true)} className="p-2 text-gray-400 hover:text-white"><PencilIcon /></button>
                    )}
                    <button onClick={() => onDelete(category.id)} className="p-2 text-red-400 hover:text-red-500"><TrashIcon /></button>
                </div>
                )}
            </div>

            {/* רשימת המוצרים */}
            <div className="space-y-2">
                {/* כותרות עמודות */}
                {products.length > 0 && (
                    <div className="bg-gray-700 p-3 rounded-lg flex flex-col md:flex-row gap-4 items-center justify-between text-white font-semibold">
                        <div className="flex-1 text-right">
                            <p>תיאור</p>
                        </div>
                        <div className="flex-1 text-right">
                            <p>מק"ט</p>
                        </div>
                        <div className="flex-1 text-right">
                            <p>מחיר</p>
                        </div>
                        <div className="flex-1 text-right">
                            <p>מלאי</p>
                        </div>
                        <div className="flex-1 text-right">
                            <p>{category.customFieldName}</p>
                        </div>
                        {!isEditMode && (
                            <div className="w-32 text-center">
                                <p>כמות</p>
                            </div>
                        )}
                        {isEditMode && (
                            <div className="w-20 text-center">
                                <p>פעולות</p>
                            </div>
                        )}
                    </div>
                )}
                
                {products.map(p => {
                    const selectedItem = selectedItems.find(item => item.id === p.id);
                    return (
                        <ProductItem 
                            key={p.id} 
                            product={p} 
                            category={category} 
                            onSave={onProductUpdate} 
                            onDelete={onProductDelete} 
                            isEditMode={isEditMode} 
                            addToCart={addToCart} 
                            selectedQuantity={selectedItem ? selectedItem.quantity : 0} 
                            updateQuantity={updateQuantity}
                        />
                    );
                })}
            </div>

            {/* כפתורי הוספה ויבוא */}
            {isEditMode && (
                <div className="mt-4 flex flex-col md:flex-row gap-2">
                    <button onClick={() => onProductAdd(category.id)} className="flex items-center justify-center bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 flex-1">
                        <PlusIcon /> הוסף מוצר ידנית
                    </button>
                    <input type="file" accept=".xlsx, .xls, .csv" ref={fileInputRef} onChange={handleFileImport} className="hidden" />
                    <button onClick={() => fileInputRef.current.click()} className="flex items-center justify-center bg-green-500 text-white py-2 px-4 rounded-lg hover:bg-green-600 flex-1">
                        <UploadIcon /> יבוא מאקסל
                    </button>
                </div>
            )}
        </div>
    );
}

// --- הקומפוננטה הראשית ---
export default function ExcelExport({ effectiveUserId, isEditMode }) {
    const [categories, setCategories] = useState([]);
    const [products, setProducts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [mainTitle, setMainTitle] = useState('יבוא מוצרים מאקסל');
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [priceFrom, setPriceFrom] = useState('');
    const [priceTo, setPriceTo] = useState('');
    const [stockFrom, setStockFrom] = useState('');
    const [stockTo, setStockTo] = useState('');
    const [selectedItems, setSelectedItems] = useState([]);
    const [showOrderPreview, setShowOrderPreview] = useState(false);
    const [flyingCart, setFlyingCart] = useState(null);
    const cartButtonRef = React.useRef(null);
    const [pendingCartAnimation, setPendingCartAnimation] = useState(null);
    const [orderFlowStep, setOrderFlowStep] = useState('ordering');

    // האזנה לקטגוריות
    useEffect(() => {
        if (!effectiveUserId) return;
        const q = collection(db, 'users', effectiveUserId, 'excelProductCategories');
        const unsubscribe = onSnapshot(q, snapshot => {
            const cats = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setCategories(cats);
            setIsLoading(false);
        });
        return () => unsubscribe();
    }, [effectiveUserId]);

    // האזנה למוצרים
    useEffect(() => {
        if (!effectiveUserId) return;
        const q = collection(db, 'users', effectiveUserId, 'excelProducts');
        const unsubscribe = onSnapshot(q, snapshot => {
            const prods = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setProducts(prods);
        });
        return () => unsubscribe();
    }, [effectiveUserId]);

    // האזנה לכותרת הראשית
    useEffect(() => {
        if (!effectiveUserId) return;
        const docRef = doc(db, 'users', effectiveUserId, 'excelSettings', 'mainTitle');
        const unsubscribe = onSnapshot(docRef, (doc) => {
            if (doc.exists() && doc.data().title) {
                setMainTitle(doc.data().title);
            }
        });
        return () => unsubscribe();
    }, [effectiveUserId]);

    // אפקט שמפעיל את האנימציה כאשר כפתור שלם מופיע
    useEffect(() => {
        if (pendingCartAnimation && cartButtonRef.current && pendingCartAnimation.plusCoords) {
            const { product, plusCoords } = pendingCartAnimation;
            const endRect = cartButtonRef.current.getBoundingClientRect();
            setFlyingCart({
                x1: plusCoords.x,
                y1: plusCoords.y,
                x2: endRect.left + endRect.width / 2,
                y2: endRect.top + endRect.height / 2,
                id: product.id
            });
            setTimeout(() => setFlyingCart(null), 2000);
            setPendingCartAnimation(null);
        }
    }, [pendingCartAnimation, cartButtonRef.current]);

    // פונקציה לסינון מוצרים לפי חיפוש
    const filteredProducts = products.filter(product => {
        // סינון לפי טקסט חיפוש
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            const matchesSearch = (
                product.name?.toLowerCase().includes(searchLower) ||
                product.sku?.toLowerCase().includes(searchLower) ||
                product.customFieldValue?.toLowerCase().includes(searchLower)
            );
            if (!matchesSearch) return false;
        }

        // סינון לפי מחיר
        if (priceFrom && product.price < parseFloat(priceFrom)) return false;
        if (priceTo && product.price > parseFloat(priceTo)) return false;

        // סינון לפי מלאי
        if (stockFrom && product.stock < parseInt(stockFrom)) return false;
        if (stockTo && product.stock > parseInt(stockTo)) return false;

        return true;
    });

    // --- פונקציות ניהול ---
    const handleAddCategory = async () => {
        const newCategory = {
            name: "קטגוריה חדשה",
            customFieldName: "שדה מותאם"
        };
        await addDoc(collection(db, 'users', effectiveUserId, 'excelProductCategories'), newCategory);
    };

    const handleUpdateCategory = async (id, data) => {
        await updateDoc(doc(db, 'users', effectiveUserId, 'excelProductCategories', id), data);
    };

    const handleDeleteCategory = async (id) => {
        if (window.confirm("האם למחוק את הקטגוריה וכל המוצרים שבה?")) {
            // מחיקת הקטגוריה וכל המוצרים שלה ב-batch
            const batch = writeBatch(db);
            batch.delete(doc(db, 'users', effectiveUserId, 'excelProductCategories', id));
            products.filter(p => p.categoryId === id).forEach(p => {
                batch.delete(doc(db, 'users', effectiveUserId, 'excelProducts', p.id));
            });
            await batch.commit();
        }
    };

    const handleAddProduct = async (categoryId) => {
        const newProduct = {
            categoryId,
            name: "מוצר חדש",
            sku: "",
            price: 0,
            stock: 0,
            customFieldValue: ""
        };
        await addDoc(collection(db, 'users', effectiveUserId, 'excelProducts'), newProduct);
    };

    const handleUpdateProduct = async (id, data) => {
        await updateDoc(doc(db, 'users', effectiveUserId, 'excelProducts', id), data);
    };

    const handleDeleteProduct = async (id) => {
        if (window.confirm("האם למחוק את המוצר?")) {
        await deleteDoc(doc(db, 'users', effectiveUserId, 'excelProducts', id));
        }
    };

    const handleExcelImport = async (categoryId, data) => {
        // דלג על השורה הראשונה (כותרות)
        const productsToAdd = [];
        for (let i = 1; i < data.length; i++) {
            const row = data[i];
            if (row.length >= 4) {
                // נוסיף תמיכה בעמודה שישית (F) - URL של תמונה
                const [name, sku, price, stock, customValue, imageUrl] = row;
                if (name && String(name).trim()) {
                    const category = categories.find(c => c.id === categoryId);
                    const newProduct = {
                        categoryId,
                        name: String(name).trim(),
                        sku: sku ? String(sku).trim() : "",
                        price: parseFloat(price) || 0,
                        stock: parseInt(stock) || 0,
                        customFieldValue: customValue ? String(customValue).trim() : "",
                        imageUrl: imageUrl ? String(imageUrl).trim() : ""
                    };
                    productsToAdd.push(newProduct);
                }
            }
        }

        // הוסף את כל המוצרים ב-batch
            const batch = writeBatch(db);
        productsToAdd.forEach(product => {
            const docRef = doc(collection(db, 'users', effectiveUserId, 'excelProducts'));
            batch.set(docRef, product);
            });
            await batch.commit();

        alert(`יובאו ${productsToAdd.length} מוצרים בהצלחה!`);
    };

    // פונקציה לנקות את כל הסינונים
    const clearAllFilters = () => {
        setSearchTerm('');
        setPriceFrom('');
        setPriceTo('');
        setStockFrom('');
        setStockTo('');
    };

    // פונקציה לשמירת הכותרת הראשית
    const saveMainTitle = async (title) => {
        try {
            await setDoc(doc(db, 'users', effectiveUserId, 'excelSettings', 'mainTitle'), { title: title }, { merge: true });
        } catch (error) {
            console.error('שגיאה בשמירת הכותרת:', error);
        }
    };

    // פונקציות לניהול בחירת פריטים
    const addToCart = (product, event, currentQuantity = 0) => {
        const isFirstItem = currentQuantity === 0;
        let plusCoords = null;
        if (event && event.currentTarget) {
            const rect = event.currentTarget.getBoundingClientRect();
            plusCoords = {
                x: rect.left + rect.width / 2,
                y: rect.top + rect.height / 2
            };
        }
        setSelectedItems(prev => {
            const existing = prev.find(item => item.id === product.id);
            if (existing) {
                return prev.map(item => 
                    item.id === product.id 
                        ? { ...item, quantity: item.quantity + 1 }
                        : item
                );
            } else {
                return [...prev, { ...product, quantity: 1 }];
            }
        });
        if (isFirstItem && plusCoords) {
            if (cartButtonRef.current) {
                const endRect = cartButtonRef.current.getBoundingClientRect();
                setFlyingCart({
                    x1: plusCoords.x,
                    y1: plusCoords.y,
                    x2: endRect.left + endRect.width / 2,
                    y2: endRect.top + endRect.height / 2,
                    id: product.id
                });
                setTimeout(() => setFlyingCart(null), 2000);
            } else {
                setPendingCartAnimation({ product, plusCoords });
            }
        }
    };

    const removeFromCart = (productId) => {
        setSelectedItems(prev => prev.filter(item => item.id !== productId));
    };

    const updateQuantity = (productId, quantity) => {
        if (quantity <= 0) {
            removeFromCart(productId);
        } else {
            setSelectedItems(prev => 
                prev.map(item => 
                    item.id === productId 
                        ? { ...item, quantity: quantity }
                        : item
                )
            );
        }
    };

    const getTotalPrice = () => {
        return selectedItems.reduce((total, item) => total + (item.price * item.quantity), 0);
    };

    if (orderFlowStep === 'payment') {
        return (
            <PaymentOptionsScreen 
                onSelectPaymentMethod={async (paymentMethod) => {
                    try {
                        // יצירת אובייקט הזמנה
                        const orderData = {
                            name: "לקוח אקסל",
                            contact: "לא צוין",
                            items: selectedItems.map(item => ({
                                label: item.name,
                                quantity: item.quantity,
                                price: item.price,
                                parentType: 'excel'
                            })),
                            totalPrice: getTotalPrice(),
                            paymentMethod: paymentMethod,
                            notificationMethod: "לא צוין"
                        };

                        // שמירת ההזמנה להזמנות פעילות
                        const queueNumber = await addOrder(effectiveUserId, orderData);
                        console.log('Order saved with queue number:', queueNumber);
                        
                        // איפוס העגלה
                        setSelectedItems([]);
                        setOrderFlowStep('ordering');
                        
                        // הצגת הודעת אישור
                        alert(`ההזמנה נשמרה בהצלחה! מספר תור: ${queueNumber}`);
                    } catch (error) {
                        console.error('Error saving order:', error);
                        alert('שגיאה בשמירת ההזמנה. נסה שוב.');
                    }
                }}
                onReturnToOrdering={() => setOrderFlowStep('ordering')}
            />
        );
    }

    if (isLoading) {
        return <div className="text-center text-white">טוען...</div>;
    }

    return (
        <div className="bg-gray-800 rounded-lg p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
                {isEditingTitle ? (
                    <div className="flex gap-2 items-center flex-1">
                        <input 
                            type="text" 
                            value={mainTitle} 
                            onChange={(e) => setMainTitle(e.target.value)} 
                            className="bg-gray-700 p-2 rounded text-white text-2xl font-bold flex-1" 
                            placeholder="כותרת ראשית" 
                        />
                        <button onClick={() => {saveMainTitle(mainTitle); setIsEditingTitle(false);}} className="p-2 text-green-400 hover:text-green-500"><CheckIcon /></button>
                        <button onClick={() => {setMainTitle('יבוא מוצרים מאקסל'); saveMainTitle('יבוא מוצרים מאקסל'); setIsEditingTitle(false);}} className="p-2 text-gray-400 hover:text-white"><XIcon /></button>
                    </div>
                ) : (
                    <h2 className="text-2xl font-bold text-amber-400">{mainTitle}</h2>
                )}
                {isEditMode && !isEditingTitle && (
                    <button onClick={() => setIsEditingTitle(true)} className="p-2 text-gray-400 hover:text-white"><PencilIcon /></button>
                )}
            </div>
            
            {/* שדה חיפוש */}
            <div className="mb-4">
                <div className="relative">
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="חיפוש מוצרים..."
                        className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-amber-500 focus:outline-none"
                    />
                    {searchTerm && (
                        <button
                            onClick={() => setSearchTerm('')}
                            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                        >
                            ✕
                        </button>
                    )}
                </div>
                
                {/* סינוני מחיר ומלאי */}
                <div className="flex gap-4 mt-3">
                    {/* סינון מחיר */}
                    <div className="flex items-center gap-2 flex-1">
                        <span className="text-white text-sm">מחיר:</span>
                        <input
                            type="number"
                            value={priceFrom}
                            onChange={(e) => setPriceFrom(e.target.value)}
                            placeholder="מ"
                            className="bg-gray-700 text-white px-3 py-1 rounded border border-gray-600 focus:border-amber-500 focus:outline-none w-20"
                        />
                        <span className="text-white text-sm">עד</span>
                        <input
                            type="number"
                            value={priceTo}
                            onChange={(e) => setPriceTo(e.target.value)}
                            placeholder="עד"
                            className="bg-gray-700 text-white px-3 py-1 rounded border border-gray-600 focus:border-amber-500 focus:outline-none w-20"
                        />
                    </div>
                    
                    {/* סינון מלאי */}
                    <div className="flex items-center gap-2 flex-1">
                        <span className="text-white text-sm">מלאי:</span>
                        <input
                            type="number"
                            value={stockFrom}
                            onChange={(e) => setStockFrom(e.target.value)}
                            placeholder="מ"
                            className="bg-gray-700 text-white px-3 py-1 rounded border border-gray-600 focus:border-amber-500 focus:outline-none w-20"
                        />
                        <span className="text-white text-sm">עד</span>
                        <input
                            type="number"
                            value={stockTo}
                            onChange={(e) => setStockTo(e.target.value)}
                            placeholder="עד"
                            className="bg-gray-700 text-white px-3 py-1 rounded border border-gray-600 focus:border-amber-500 focus:outline-none w-20"
                        />
                    </div>
                </div>
                
                {(searchTerm || priceFrom || priceTo || stockFrom || stockTo) && (
                    <div className="mt-2 flex justify-between items-center">
                        <div className="text-sm text-gray-400">
                            נמצאו {filteredProducts.length} מוצרים
                        </div>
                        <button
                            onClick={clearAllFilters}
                            className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 rounded text-sm"
                        >
                            נקה סינון
                        </button>
                    </div>
                )}
            </div>

            {/* הצגת מוצרים מסוננים */}
            {(searchTerm || priceFrom || priceTo || stockFrom || stockTo) && (
                <div className="mb-6">
                    <h3 className="text-lg font-semibold mb-3 text-white">תוצאות חיפוש:</h3>
                    
                    {/* כותרות עמודות לתוצאות חיפוש */}
                    {filteredProducts.length > 0 && (
                        <div className="bg-gray-700 p-3 rounded-lg flex justify-between items-center text-white font-semibold mb-2">
                            <div className="flex-1 text-right">
                                <p>תיאור</p>
                            </div>
                            <div className="flex-1 text-right">
                                <p>מק"ט</p>
                            </div>
                            <div className="flex-1 text-right">
                                <p>מחיר</p>
                            </div>
                            <div className="flex-1 text-right">
                                <p>מלאי</p>
                            </div>
                            <div className="flex-1 text-right">
                                <p>קטגוריה</p>
                            </div>
                            <div className="flex-1 text-right">
                                <p>שדה מותאם</p>
                            </div>
                            {!isEditMode && (
                                <div className="w-32 text-center">
                                    <p>כמות</p>
                                </div>
                            )}
                            {isEditMode && (
                                <div className="w-20 text-center">
                                    <p>פעולות</p>
                                </div>
                            )}
                        </div>
                    )}
                    
                    <div className="space-y-2">
                        {filteredProducts.map(product => {
                            const category = categories.find(c => c.id === product.categoryId);
                            const selectedItem = selectedItems.find(item => item.id === product.id);
                            return (
                                <div key={product.id} className="bg-gray-700/50 p-3 rounded-lg flex justify-between items-center">
                                    <div className="flex-1 text-right">
                                        <p className="font-bold">{product.name}</p>
                                    </div>
                                    <div className="flex-1 text-right">
                                        <p className="text-sm text-gray-400">{product.sku}</p>
                                    </div>
                                    <div className="flex-1 text-right">
                                        <p>{product.price} ₪</p>
                                    </div>
                                    <div className="flex-1 text-right">
                                        <p className="text-sm text-gray-400">{product.stock}</p>
                                    </div>
                                    <div className="flex-1 text-right">
                                        <p>{category?.name || 'לא ידוע'}</p>
                                    </div>
                                    <div className="flex-1 text-right">
                                        <p>{product.customFieldValue}</p>
                                    </div>
                                    {!isEditMode && (
                                        <div className="flex items-center gap-2 w-32 justify-center">
                                            {selectedItem && selectedItem.quantity > 0 && (
                                                <>
                                                    <button onClick={() => updateQuantity(product.id, selectedItem.quantity - 1)} className="bg-gray-600 hover:bg-gray-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">-</button>
                                                    <span className="text-white font-bold">{selectedItem.quantity}</span>
                                                </>
                                            )}
                                            <button onClick={(e) => addToCart(product, e, selectedItem ? selectedItem.quantity : 0)} className="bg-gray-600 hover:bg-gray-500 text-white px-3 py-1 rounded text-sm">+</button>
                                        </div>
                                    )}
                                    {isEditMode && (
                                        <div className="flex gap-2 w-20 justify-center">
                                            <button onClick={() => handleUpdateProduct(product.id, { ...product, name: product.name + ' (עודכן)' })} className="p-2 text-gray-400 hover:text-white"><PencilIcon /></button>
                                            <button onClick={() => handleDeleteProduct(product.id)} className="p-2 text-red-400 hover:text-red-500"><TrashIcon /></button>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* הצגת קטגוריות ומוצרים רגיל */}
            {!(searchTerm || priceFrom || priceTo || stockFrom || stockTo) && (
                <>
                    {categories.map(category => {
                        const categoryProducts = products.filter(p => p.categoryId === category.id);
                        return (
                <CategorySection
                                key={category.id}
                                category={category}
                                products={categoryProducts}
                    onSave={handleUpdateCategory}
                    onDelete={handleDeleteCategory}
                    onProductUpdate={handleUpdateProduct}
                    onProductDelete={handleDeleteProduct}
                                onProductAdd={handleAddProduct}
                    onExcelImport={handleExcelImport}
                    isEditMode={isEditMode}
                                addToCart={addToCart}
                                selectedItems={selectedItems}
                                updateQuantity={updateQuantity}
                            />
                        );
                    })}

                    {isEditMode && (
                        <button
                            onClick={handleAddCategory}
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg font-semibold"
                        >
                            + הוסף קטגוריה חדשה
                        </button>
                    )}
                </>
            )}

            {/* כפתור שלם */}
            {!isEditMode && selectedItems.length > 0 && (
                <div className="fixed bottom-4 left-4 right-4 z-40">
                    <div className="bg-gray-800 border border-gray-600 rounded-lg p-4 shadow-lg">
                        <div className="flex justify-between items-center">
                            <div>
                                <p className="text-white font-bold">סה"כ לתשלום: {getTotalPrice()} ₪</p>
                                <p className="text-gray-400 text-sm">{selectedItems.length} פריטים נבחרו</p>
                            </div>
                            <button
                                ref={cartButtonRef}
                                onClick={() => setShowOrderPreview(true)}
                                className="bg-amber-500 hover:bg-amber-600 text-black font-bold py-3 px-6 rounded-lg"
                            >
                                שלם
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* קומפוננטת תצוגה מקדימה של ההזמנה */}
            {showOrderPreview && (
                <OrderPreviewModal
                    isOpen={showOrderPreview}
                    onClose={() => setShowOrderPreview(false)}
                    selectedItems={selectedItems}
                    totalPrice={getTotalPrice()}
                    onConfirmOrder={(items) => {
                        setSelectedItems(items);
                        setShowOrderPreview(false);
                        setOrderFlowStep('payment');
                    }}
                />
            )}

            {/* אנימציית עגלה עפה */}
            {flyingCart && (
                <div 
                    className="flying-cart-anim"
                    style={{
                        left: 0,
                        top: 0,
                        width: 56,
                        height: 56,
                        '--arc-path': `path('M ${flyingCart.x1} ${flyingCart.y1} Q ${(flyingCart.x1 + flyingCart.x2) / 2} ${flyingCart.y1 - 180}, ${flyingCart.x2} ${flyingCart.y2}')`
                    }}
                >
                    <div className="bg-amber-500 text-white p-3 rounded-full shadow-2xl border-4 border-amber-300 flex items-center justify-center aspect-square w-14 h-14">
                        <CartIcon />
                    </div>
                </div>
            )}
        </div>
    );
}

// קומפוננטת תצוגה מקדימה של ההזמנה
function OrderPreviewModal({ isOpen, onClose, selectedItems, totalPrice, onConfirmOrder }) {
    const [items, setItems] = React.useState(selectedItems);
    React.useEffect(() => { setItems(selectedItems); }, [selectedItems]);

    const handleChangeQuantity = (id, delta) => {
        setItems(prev => {
            const updated = prev.map(item =>
                item.id === id ? { ...item, quantity: item.quantity + delta } : item
            ).filter(item => item.quantity > 0);
            return updated;
        });
    };
    const calcTotal = () => items.reduce((sum, item) => sum + item.price * item.quantity, 0);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50 p-4">
            <div className="bg-gray-800 text-white rounded-xl p-6 shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
                <h3 className="text-xl font-bold text-amber-400 mb-4">תצוגה מקדימה של ההזמנה</h3>
                <div className="space-y-3 mb-6">
                    {items.map(item => (
                        <div key={item.id} className="flex justify-between items-center bg-gray-700 p-3 rounded-lg">
                            <div>
                                <p className="font-bold">{item.name}</p>
                                <p className="text-sm text-gray-400">{item.sku}</p>
                            </div>
                            <div className="flex items-center gap-2">
                                <button onClick={() => handleChangeQuantity(item.id, -1)} className="bg-gray-600 hover:bg-gray-500 text-white w-7 h-7 rounded-full flex items-center justify-center text-lg">-</button>
                                <span className="text-white font-bold w-8 text-center">{item.quantity}</span>
                                <button onClick={() => handleChangeQuantity(item.id, 1)} className="bg-gray-600 hover:bg-gray-500 text-white w-7 h-7 rounded-full flex items-center justify-center text-lg">+</button>
                            </div>
                            <div className="text-right min-w-[90px]">
                                <p className="font-bold">{item.quantity} x {item.price} ₪</p>
                                <p className="text-amber-400 font-bold">{item.quantity * item.price} ₪</p>
                            </div>
                        </div>
                    ))}
                    {items.length === 0 && <div className="text-center text-gray-400">אין פריטים בהזמנה</div>}
                </div>
                <div className="border-t border-gray-600 pt-4 mb-6">
                    <div className="flex justify-between items-center text-xl font-bold">
                        <span>סה"כ לתשלום:</span>
                        <span className="text-amber-400">{calcTotal()} ₪</span>
                    </div>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={() => items.length > 0 && onConfirmOrder(items)}
                        className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-6 rounded-lg flex-1 disabled:opacity-50"
                        disabled={items.length === 0}
                    >
                        אישור הזמנה
                    </button>
                    <button onClick={onClose} className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-6 rounded-lg flex-1">
                        ביטול
                    </button>
                </div>
            </div>
        </div>
    );
}
